import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  Grid2 as Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FilmStrip, Image as ImageIcon, Paperclip, X } from "phosphor-react";
import AttachMediaModal from "./AttachMediaModal";
import MediaDrawerGridItem from "./MediaDrawerGridItem";
import useStyles from "./MediaDrawer.styles";
import { fpost, exportFileWithXhr, fget, fpatch } from "../../../API/callsAPI";

const drawerPaperSx = {
  width: { xs: "100%", sm: 600 },
  maxWidth: "100vw",
  bgcolor: "#1B1D28",
  color: "#F9F9F9",
  borderLeft: "1px solid #606479",
  borderTop: "1px solid #606479",
  boxSizing: "border-box",
  top: "65px",
  "@media (min-width: 1280px)": {
    top: "80px",
  },
  padding: "24px",
  bottom: 0,
  height: "auto",
};

const drawerBackdropSx = {
  backgroundColor: "rgba(0, 0, 0, 0.22)",
  top: "65px",
  "@media (min-width: 1280px)": {
    top: "80px",
  },
};

const MediaDrawer = ({
  open,
  onClose,
  type,
  pageId,
  fieldIdentifier,
  selectedMedia,
  handleRefresh,
}) => {
  const classes = useStyles();
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const pollIntervalRef = useRef(null);
  const filesRef = useRef(files); // Keep ref in sync with state
  const fileCount = files.length;

  useEffect(() => {
    filesRef.current = files; // Update ref whenever files changes
  }, [files]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const queueImages = async (queueId) => {
    const res = await fpost({
      url: `trackables/trackable-field/values/${queueId}/thumbnail/`,
    });
    if (res?.status === 200 || res?.status === 201) {
      return res.data;
    }
    return null;
  };

  const pollQueueStatus = () => {
    const rowIndex = selectedMedia?.row?.id;
    const checkThumbnailStatus = async () => {
      try {
        const res = await fget({
          url: `trackables/custom-page/${pageId}/thumbnails/`,
        });
        if (res?.status !== 200 && res?.status !== 201) return;
        const thumbnailData = res.data || [];
        const thumbnailrows = thumbnailData.filter(
          (item) => item.id === rowIndex,
        );
        let allFilesUploadedstate = false;
        let newThumbnailsUploaded = [];
        for (const row of thumbnailrows) {
          const thumbnailsObj = row?.[fieldIdentifier]?.[0] || {};

          const allFilesUploaded =
            filesRef.current.length > 0 &&
            filesRef.current.every(
              (file) =>
                thumbnailsObj.hasOwnProperty(file.name) &&
                thumbnailsObj[file.name],
            );
          if (allFilesUploaded) {
            allFilesUploadedstate = true;
            newThumbnailsUploaded = Object.entries(thumbnailsObj).map(
              ([key, value]) => ({
                url: value,
                name: key,
              }),
            );
            break;
          }
        }

        if (allFilesUploadedstate) {
          setFiles([]);
          setUploadedFiles(newThumbnailsUploaded);
          setUploadSuccess(true); // Mark uploads as successful
          stopPolling();
        }
      } catch (error) {
        console.log(error);
      }
    };

    stopPolling();
    checkThumbnailStatus();
    pollIntervalRef.current = setInterval(checkThumbnailStatus, 5000);
  };

  const updateFileStatus = (fileName, status) => {
    setFiles((prevFiles) =>
      prevFiles.map((entry) =>
        entry.name === fileName ? { ...entry, status } : entry,
      ),
    );
  };

  const handleFilesSelected = async (selectedFiles) => {
    setFiles(selectedFiles.map((file) => ({ file, name: file.name })));
    const fileNames = selectedFiles.map((file) => file.name);
    if (type === "custompage" && fieldIdentifier) {
      try {
        const payload = {};
        payload[fieldIdentifier] = fileNames;
        const res = await fpatch({
          url: `trackables/custom-page/${pageId}/update-row/${selectedMedia.row.id}/`,
          data: payload,
        });
        if (res?.status === 200 || res?.status === 201) {
          const queueId = res.data[fieldIdentifier].id;
          const signedUrlsObj = res.data[fieldIdentifier].signed_urls;
          for (const fileName in signedUrlsObj) {
            const signedUrl = signedUrlsObj[fileName];
            const file = selectedFiles.find((f) => f.name === fileName);
            if (!(file instanceof File)) continue;

            await exportFileWithXhr({
              url: signedUrl,
              data: file,
              updateProgressBar: () => {
                updateFileStatus(fileName, "uploading");
              },
              transferComplete: () => {},
              transferFailed: () => {
                updateFileStatus(fileName, "failed");
              },
              transferCanceled: () => {
                updateFileStatus(fileName, "canceled");
              },
            });
          }
          await queueImages(queueId);
          pollQueueStatus();
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      //main
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        stopPolling();
        // Refresh parent data if files were successfully uploaded
        if (uploadSuccess) {
          handleRefresh?.();
          setUploadSuccess(false); // Reset flag after refresh
        }
        onClose();
      }}
      slotProps={{
        backdrop: { sx: drawerBackdropSx },
        paper: { sx: drawerPaperSx },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 500, fontSize: 18 }}>
            {selectedMedia.row.name}
          </Typography>
          <IconButton
            onClick={() => {
              stopPolling();
              // Refresh parent data if files were successfully uploaded
              if (uploadSuccess) {
                handleRefresh?.();
                setUploadSuccess(false); // Reset flag after refresh
              }
              onClose();
            }}
            aria-label="Close media drawer"
            size="small"
            sx={{
              color: "#F9F9F9",
              "&:hover": {
                color: "#F9F9F9",
                bgcolor: "rgba(255,255,255,0.06)",
              },
            }}
          >
            <X size={24} />
          </IconButton>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexShrink: 0, gap: 1, flexWrap: "wrap" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexWrap: "wrap" }}
          >
            <Typography sx={{ fontSize: 14, color: "#F9F9F9" }}>
              Files {fileCount}
            </Typography>
            <Button
              className={classes.filterChip}
              startIcon={<FilmStrip size={18} />}
              disableRipple
            >
              Videos
            </Button>
            <Button
              className={classes.filterChip}
              startIcon={<ImageIcon size={18} />}
              disableRipple
            >
              Images
            </Button>
          </Stack>
          <Button
            className={classes.attachButton}
            startIcon={<Paperclip size={18} color="#B1B5C9" />}
            disableRipple
            onClick={() => setIsAttachModalOpen(true)}
          >
            Attach
          </Button>
        </Stack>

        <Box sx={{ flex: 1, overflow: "auto", mt: 2 }}>
          <Grid container spacing={2} gap={2}>
            {uploadedFiles.length > 0
              ? uploadedFiles.map((file, index) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={file + index}>
                    <MediaDrawerGridItem
                      isUploading={false}
                      isVideo={/\.(mp4|avi|mov|webm)(\?|$)/i.test(file.url)}
                      fileName={file.name}
                      previewSrc={file.url}
                      status={"completed"}
                    />
                  </Grid>
                ))
              : selectedMedia?.row?.[selectedMedia?.fieldName]?.length > 0 &&
                selectedMedia.row[selectedMedia.fieldName].map(
                  (file, index) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={file + index}>
                      <MediaDrawerGridItem
                        isUploading={false}
                        isVideo={/\.(mp4|avi|mov|webm)(\?|$)/i.test(file)}
                        fileName={
                          file.split("/").pop()?.split("?")[0] || "image"
                        }
                        previewSrc={file}
                        status={"completed"}
                      />
                    </Grid>
                  ),
                )}
            {files.map((entry, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={entry.name + index}>
                <MediaDrawerGridItem
                  isUploading={true}
                  isVideo={/\.(mp4|avi|mov|webm)(\?|$)/i.test(entry.name)}
                  fileName={entry.name}
                  previewSrc={URL.createObjectURL(entry.file)}
                  status={"completed"}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      <AttachMediaModal
        open={isAttachModalOpen}
        onClose={() => setIsAttachModalOpen(false)}
        onFilesSelected={handleFilesSelected}
      />
    </Drawer>
  );
};

export default MediaDrawer;
